import React, { useState } from "react";
import {
  HeaderContainer,
  HeaderChronofieldLogo,
  HeaderChronofieldLogoName,
  HeaderLogoLink,
  HeaderNavbar,
  NavLink,
  Drawer,
  BurgerMenu,
  DrawerItem,
  DrawerSubMenu,
  DrawerOverlay,
} from "./Header.style";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import UserMenu from "./UserMenu";
import MenuPopupState from "../MenuPopupState";
import LogoutConfirmation from "./LogoutConfirmation";
import ThemeToggle from "./ThemeToggle";
import { Button } from "./styles";
import { hasCreateAccess } from "../authRoles";

const NavIconTools = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const NavIconInfo = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const NavIconDates = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const NavIconCreate = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
    <rect x="3" y="3" width="18" height="18" rx="2" />
  </svg>
);

const Header = () => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const { isLoggedIn, pseudo, loading, roles } = useAuth();
  const canCreate = hasCreateAccess(roles);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  if (loading) return null;

  // For mobile: handle submenu open/close
  const handleDrawerItemClick = (idx) => {
    setOpenSubMenu(openSubMenu === idx ? null : idx);
  };

  const menuItems = [
    {
      label: "Outils",
      icon: <NavIconTools />,
      to: "/home",
      subLinks: [
        { label: "Convertisseur IRL → Erache", to: "/irl-to-erache-converter" },
        { label: "Convertisseur Erache → IRL", to: "/erache-to-irl-converter" },
        { label: "Calculateur de cubos", to: "/outils/cubo" },
        { label: "Chercheur de fleurs", to: "/outils/fleurs" },
      ],
    },
    {
      label: "Informations",
      icon: <NavIconInfo />,
      to: "/erache-infos",
      subLinks: [
        { label: "Calendrier Erachien", to: "/erache-infos" },
        { label: "Extension Wikifield", to: "/extension-wikifield" },
      ],
    },
    {
      label: "Dates",
      icon: <NavIconDates />,
      subLinks: [
        { label: "Liste des dates", to: "/list" },
        { label: "Liste des périodes", to: "/periods" },
        { label: "Frises", to: "/timelines" },
      ],
    },
    ...(canCreate
      ? [
          {
            label: "Créer",
            icon: <NavIconCreate />,
            subLinks: [
              { label: "Créer un évènement", to: "/add-event" },
              { label: "Créer une période", to: "/add-period" },
              { label: "Créer une frise", to: "/add-timeline" },
            ],
          },
        ]
      : []),
  ];

  return (
    <HeaderContainer>
      <HeaderLogoLink to="/home" aria-label="ChronoField accueil">
        <HeaderChronofieldLogo />
        <HeaderChronofieldLogoName>
          Chrono<span>Field</span>
        </HeaderChronofieldLogoName>
      </HeaderLogoLink>

      {/* Desktop Navbar */}
      <HeaderNavbar>
        {/* Groupe de gauche : menus principaux */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, paddingLeft: 16 }}>
          <MenuPopupState
            label="Outils"
            icon={<NavIconTools />}
            items={[
              { label: "Convertisseur IRL vers Erache", to: "/irl-to-erache-converter" },
              { label: "Convertisseur Erache vers IRL", to: "/erache-to-irl-converter" },
              { label: "Calculateur de cubos", to: "/outils/cubo" },
              { label: "Chercheur de fleurs", to: "/outils/fleurs" },
            ]}
          />
          <MenuPopupState
            label="Informations"
            icon={<NavIconInfo />}
            items={[
              { label: "Calendrier Erachien", to: "/erache-infos" },
              { label: "Extension Wikifield", to: "/extension-wikifield" },
            ]}
          />
          <MenuPopupState
            label="Dates"
            icon={<NavIconDates />}
            items={[
              { label: "Liste des évènements", to: "/list" },
              { label: "Liste des périodes", to: "/periods" },
              { label: "Frises", to: "/timelines" },
            ]}
          />
          {canCreate && (
            <MenuPopupState
              label="Créer"
              icon={<NavIconCreate />}
              items={[
                { label: "Créer un évènement", to: "/add-event" },
                { label: "Créer une période", to: "/add-period" },
                { label: "Créer une frise", to: "/add-timeline" },
              ]}
            />
          )}
        </div>

        {/* Groupe de droite : auth ou user menu */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ThemeToggle />
          {!isLoggedIn ? (
            <>
              <Button as={Link} to="/login">
                Se connecter
              </Button>
              <Button as={Link} to="/register">
                S'enregistrer
              </Button>
            </>
          ) : (
            <UserMenu pseudo={pseudo} />
          )}
        </div>
      </HeaderNavbar>

      {/* Mobile Burger Menu */}
      <BurgerMenu onClick={() => setDrawerOpen(true)}>
        <span>☰</span>
      </BurgerMenu>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <>
          <DrawerOverlay onClick={() => setDrawerOpen(false)} />
          <Drawer>
            {menuItems.map((item, idx) => (
              <DrawerItem key={item.label}>
                <div onClick={() => handleDrawerItemClick(idx)}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {item.icon}
                    {item.label}
                  </span>
                  {item.subLinks.length > 0 && (
                    <span>{openSubMenu === idx ? "▲" : "▼"}</span>
                  )}
                </div>
                {openSubMenu === idx && item.subLinks.length > 0 && (
                  <DrawerSubMenu>
                    {item.subLinks.map((sub, subIdx) => {
                      if (sub?.type === "divider") {
                        return <div key={`div-${subIdx}`} style={{ height: 1, background: "var(--border)", margin: "8px 0" }} />;
                      }
                      if (sub?.type === "section") {
                        return (
                          <div
                            key={`sec-${subIdx}`}
                            style={{
                              color: "var(--text-faint)",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              padding: "6px 2px",
                            }}
                          >
                            {sub.label}
                          </div>
                        );
                      }
                      return (
                        <NavLink
                          key={sub.label}
                          to={sub.to}
                          onClick={() => setDrawerOpen(false)}
                        >
                          {sub.label}
                        </NavLink>
                      );
                    })}
                  </DrawerSubMenu>
                )}

              </DrawerItem>
            ))}
            {isLoggedIn ? (
              <>
                <DrawerItem onClick={() => setOpenDialog(true)}>
                  Se déconnecter
                </DrawerItem>
                <LogoutConfirmation open={openDialog} onClose={() => setOpenDialog(false)}  onLogout={() => setDrawerOpen(false)} />
              </>
            ) : (
              <>
                <DrawerItem>
                  <NavLink to="/login" onClick={() => setDrawerOpen(false)}>Se connecter</NavLink>
                </DrawerItem>
                <DrawerItem>
                  <NavLink to="/register" onClick={() => setDrawerOpen(false)}>S'enregistrer</NavLink>
                </DrawerItem>
              </>
            )}
          </Drawer>
        </>
      )}
    </HeaderContainer>
  );
};

export default Header;
